/**
 * SourceChip — one rendering of "where did this come from" for leads, enquiries and contacts.
 *
 * The channels people scan for (WhatsApp, AI, Chat widget, Website) share ONE neutral look —
 * a slate pill, told apart by icon — wherever they appear (lead list, detail, enquiry history,
 * contact page, appointments). Everything else is a quiet outlined chip with a proper label
 * (raw values like `google_sheets` or `ai_tool_call` are not UI copy).
 */
import React from 'react';
import { Chip, ChipProps } from '@mui/material';
import { AutoAwesome, ChatBubbleOutline, Language, WhatsApp } from '@mui/icons-material';

/**
 * One quiet slate look for every channel — the icon is what tells them apart. These are the same
 * slate tokens the theme already uses (borders #e2e8f0, secondary text #64748b, page #f8fafc), so
 * a table full of chips reads as one calm column instead of a rainbow.
 */
const NEUTRAL = { bg: '#f1f5f9', border: '#e2e8f0', text: '#334155', icon: '#64748b' } as const;

type Style = typeof NEUTRAL & { Icon: React.ElementType; label: string };

const STYLES: Record<string, Style> = {
  whatsapp: { ...NEUTRAL, Icon: WhatsApp, label: 'WhatsApp' },
  ai: { ...NEUTRAL, Icon: AutoAwesome, label: 'AI' },
  chat_widget: { ...NEUTRAL, Icon: ChatBubbleOutline, label: 'Chat widget' },
  website: { ...NEUTRAL, Icon: Language, label: 'Website' },
};

/** Raw source / channel / ingestion values -> the styled channel they belong to. */
const ALIASES: Record<string, string> = {
  whatsapp: 'whatsapp',
  conversation_flow_bridge: 'whatsapp',
  ai: 'ai',
  ai_tool_call: 'ai',
  chat_widget: 'chat_widget',
  public_chat_widget: 'chat_widget',
  webchat: 'chat_widget',
  website: 'website',
  public_lead_form: 'website',
  crm_webhook_test_form: 'website',
};

const PLAIN_LABELS: Record<string, string> = {
  meta: 'Meta Ads',
  hubspot: 'HubSpot',
  google_sheets: 'Google Sheets',
  excel: 'Excel import',
  api: 'API',
  manual: 'Manual',
  campaign: 'Campaign',
  instagram: 'Instagram',
};

const normalise = (v?: string | null): string =>
  (v ?? '').toLowerCase().trim().replace(/[\s-]+/g, '_');

/** The styled channel for a raw value (`whatsapp_flow` counts as WhatsApp too), or undefined. */
const channelOf = (source?: string | null): string | undefined => {
  const key = normalise(source);
  return ALIASES[key] ?? (key.startsWith('whatsapp') ? 'whatsapp' : undefined);
};

/** True for the WhatsApp channel however it is spelled. */
export const isWhatsAppSource = (source?: string | null): boolean => channelOf(source) === 'whatsapp';

export const sourceLabel = (source?: string | null): string => {
  if (!source) return '';
  const ch = channelOf(source);
  if (ch) return STYLES[ch]!.label;
  const key = normalise(source);
  return PLAIN_LABELS[key] ?? source.charAt(0).toUpperCase() + source.slice(1).replace(/_/g, ' ');
};

interface Props extends Omit<ChipProps, 'label' | 'icon'> {
  /** The raw source/channel value, e.g. `whatsapp`, `website`, `ai_tool_call`. */
  source: string;
  /** Text to show instead of the standard label (e.g. a campaign name). The styling is kept. */
  label?: string;
}

const SourceChip: React.FC<Props> = ({ source, label, sx, ...rest }) => {
  const text = label || sourceLabel(source);
  const ch = channelOf(source);
  const st = ch ? STYLES[ch] : undefined;
  if (st) {
    const { Icon } = st;
    return (
      <Chip
        size="small"
        icon={<Icon sx={{ fontSize: 14, color: `${st.icon} !important` }} />}
        label={text}
        {...rest}
        sx={[
          {
            bgcolor: st.bg,
            color: st.text,
            border: '1px solid',
            borderColor: st.border,
            fontWeight: 500,
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      />
    );
  }
  return <Chip size="small" variant="outlined" label={text} {...rest} {...(sx ? { sx } : {})} />;
};

/** How an enquiry reached us (LeadEnquiry.ingestionPath) — shown on each enquiry-history row. */
const INGESTION_LABEL: Record<string, string> = {
  external_gateway: 'API',
  csv_import: 'CSV import',
  crm_webhook_test_form: 'Web form',
  crm_webhook_meta: 'Meta Lead Ads',
  crm_webhook_hubspot: 'HubSpot',
  growth_automation: 'Growth automation',
  admin_manual: 'Added manually',
  public_lead_form: 'Web form',
  public_chat_widget: 'Chat widget',
  conversation_flow_bridge: 'WhatsApp flow',
  ai_tool_call: 'AI',
  backfill: 'Historical',
};

export const ingestionLabel = (path: string): string => INGESTION_LABEL[path] ?? sourceLabel(path);

export const IngestionChip: React.FC<{ path: string }> = ({ path }) => {
  const label = ingestionLabel(path);
  return channelOf(path) ? (
    <SourceChip source={path} label={label} />
  ) : (
    <Chip size="small" label={label} />
  );
};

export default SourceChip;
